import React, { useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Alert,
  Flex,
} from "antd";
import dayjs from "dayjs";
import api from "../../api";
import { useToast } from "../../components/toast/toast";

const { RangePicker } = DatePicker;

export default function CreateCampaignDrawer({
  open,
  onClose,
  record,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const isEdit = !!record?.id;

  const [isDisabled, setIsDisabled] = useState(false);
  const [lotteries, setLotteries] = useState([]);
  const [loadingLots, setLoadingLots] = useState(false);
  const toast = useToast();

  const toOptions = (arr = []) =>
    (Array.isArray(arr) ? arr : []).map((l) => ({
      label: l.title || l.slug || l.id,
      value: l.id,
    }));

  const fetchLotteries = async () => {
    try {
      setLoadingLots(true);
      const params = {
        page: 1,
        limit: 200,
        visibility: "public",
        status_in: ["upcoming", "ongoing", "drawing"],
      };

      const res = await api.get_lottery_list?.(params);

      // console.log("res -->", res);
      const payload = res?.data ?? res;
      const items = payload.data || [];

      setLotteries(items);
    } catch (e) {
      // console.warn("Lottery options fetch failed:", e?.message);
      setLotteries([]);
    } finally {
      setLoadingLots(false);
    }
  };

  useEffect(() => {
    if (open) fetchLotteries();
  }, [open]);

  useEffect(() => {
    if (record && open) {
      const win = [];
      if (record.start_at) win.push(dayjs(record.start_at));
      if (record.end_at) win.push(dayjs(record.end_at));

      form.setFieldsValue({
        name: record.name,
        status: record.status ?? "draft",
        mode: record.mode ?? "THRESHOLD",
        amount: Number(record.amount ?? 0),
        bonusTickets: record.bonusTickets ?? 1,
        triggerLotteryId: record.triggerLotteryId || undefined,
        bonusLotteryId: record.bonusLotteryId || undefined,
        window: win,
      });
    } else if (open && !record) {
      form.resetFields();
      form.setFieldsValue({
        status: "draft",
        mode: "THRESHOLD",
        bonusTickets: 1,
      });
    }
  }, [record, open, form]);

  const handleClose = () => {
    form.resetFields();
    onClose?.();
  };

  const onFinish = async (values) => {
    try {
      setIsDisabled(true);
      const payload = {
        name: values.name,
        status: values.status,
        mode: values.mode, // "THRESHOLD" | "STEP"
        amount: Number(values.amount).toFixed(2),
        bonusTickets: values.bonusTickets,
        triggerLotteryId: values.triggerLotteryId || null,
        bonusLotteryId: values.bonusLotteryId,
      };

      // if (Array.isArray(values.window) && values.window.length === 2) {
      payload.start_at = values.window?.[0]
        ? values.window[0].toISOString()
        : null;
      payload.end_at = values.window?.[1]
        ? values.window[1].toISOString()
        : null;
      // }

      if (isEdit) {
        const res = await api.update_reward_campaign(record.id, payload);
        if (res.status === 200) {
          toast("Амжилттай заслаа.", "success");
          onSuccess?.();
          handleClose();
        } else {
          toast(res.data.message || "Алдаа гарлаа.", "error");
        }
      } else {
        const create = await api.create_reward_campaign(payload);
        if (create.status === 201) {
          toast("Амжилттай үүсгэлээ.", "success");
          onSuccess?.();
          handleClose();
        } else {
          toast(create.data.message || "Алдаа гарлаа.", "error");
        }
      }
    } catch (e) {
      toast(
        e?.response?.data?.message || e?.message || "Алдаа гарлаа",
        "error"
      );
    } finally {
      setIsDisabled(false);
    }
  };

  return (
    <Drawer
      title={"Урамшуулал"}
      placement="right"
      onClose={handleClose}
      open={open}
      width={500}
      extra={
        <Button
          type="primary"
          loading={isDisabled}
          disabled={isDisabled}
          onClick={() => form.submit()}
          style={{ padding: "0 40px" }}
        >
          {isEdit ? "Хадгалах" : "Үүсгэх"}
        </Button>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Горимын тайлбар"
        description={
          <>
            <div>
              <b>THRESHOLD</b>: Босго дүн давмагц 1 л удаа бонус өгнө, дахиж
              өгөхгүй.
            </div>
            <div>
              <b>STEP</b>: Босго дүн давах тутамд сугалаа өгөх. Ж: Босго=50,000₮
              тэгвэл 50,000₮-т 1 сугалаа, 100,000₮-т 2 сугалаа гэх мэт
            </div>
          </>
        }
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        // requiredMark="optional"
        requiredMark
      >
        <Form.Item
          label="Нэр"
          name="name"
          required
          rules={[{ required: true, message: "Нэр оруулна уу" }]}
          style={{ marginBottom: 10 }}
        >
          <Input
            placeholder="ж: 10-р сарын бонус"
            style={{ minHeight: "40px" }}
          />
        </Form.Item>

        <Flex gap={20}>
          <Form.Item
            label="Төлөв"
            name="status"
            rules={[{ required: true, message: "Төлөв сонгоно уу" }]}
            style={{ width: "100%", marginBottom: 10 }}
          >
            <Select
              options={[
                { label: "draft", value: "draft" },
                { label: "active", value: "active" },
                { label: "paused", value: "paused" },
                { label: "ended", value: "ended" },
              ]}
              style={{ minHeight: "40px" }}
            />
          </Form.Item>

          <Form.Item
            label="Горим"
            name="mode"
            rules={[{ required: true, message: "Горим сонгоно уу" }]}
            style={{ width: "100%", marginBottom: 10 }}
          >
            <Select
              options={[
                { label: "THRESHOLD", value: "THRESHOLD" },
                { label: "STEP", value: "STEP" },
              ]}
              style={{ minHeight: "40px" }}
            />
          </Form.Item>
        </Flex>

        <Flex gap={20}>
          <Form.Item
            label="Дүн (₮)"
            name="amount"
            rules={[{ required: true, message: "Дүн заавал" }]}
            style={{ width: "100%", marginBottom: 10 }}
          >
            <InputNumber
              min={1}
              step={1000}
              style={{ width: "100%", minHeight: "40px", padding: "5px 0" }}
              formatter={(v) => (v ? Number(v).toLocaleString("mn-MN") : "")}
              parser={(v) => (v ? v.replace(/[^\d.-]/g, "") : "")}
              placeholder="ж: 50000"
            />
          </Form.Item>

          <Form.Item
            label="Бонус тасалбар (тоо)"
            name="bonusTickets"
            rules={[{ required: true, message: "Бонусын тоо заавал" }]}
            style={{ width: "100%", marginBottom: 10 }}
          >
            <InputNumber
              min={1}
              step={1}
              style={{ width: "100%", minHeight: "40px", padding: "5px 0" }}
            />
          </Form.Item>
        </Flex>

        <div>
          <Form.Item
            label="Trigger сугалаа"
            name="triggerLotteryId"
            rules={[{ required: true, message: "Trigger сугалаа заавал" }]}
            style={{ width: "100%", marginBottom: 10 }}
            tooltip="Сонгосон бол зөвхөн энэ сугалааны худалдан авалтад үйлчилнэ. Сонгохгүй бол бүх сугалаанд."
          >
            <Select
              allowClear
              showSearch
              placeholder="Бүх сугалаанд үйлчлэх"
              loading={loadingLots}
              options={toOptions(lotteries)}
              optionFilterProp="label"
              style={{ minHeight: "40px" }}
            />
          </Form.Item>

          <Form.Item
            label="Үнэгүй өгөх сугалаа"
            name="bonusLotteryId"
            rules={[{ required: true, message: "Бонус сугалаа сонгоно уу" }]}
            style={{ width: "100%", marginBottom: 10 }}
          >
            <Select
              showSearch
              placeholder="Сонгох"
              loading={loadingLots}
              options={toOptions(lotteries)}
              optionFilterProp="label"
              style={{ minHeight: "40px" }}
            />
          </Form.Item>
        </div>

        <Form.Item
          label="Хугацааны цонх"
          name="window"
          tooltip="Хоосон үлдээвэл үргэлжилдэг кампайн болно"
        >
          <RangePicker
            style={{ width: "100%", minHeight: "40px" }}
            showTime={{ format: "HH:mm" }}
            format="YYYY-MM-DD HH:mm"
            allowClear
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
